import { useState, useCallback, useRef, useEffect } from 'react';
import { StickyNote as StickyNoteIcon, Plus, Loader2, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { StickyNoteCard } from './sticky-note-card';
import {
  useStickyNotes,
  useCreateStickyNote,
  useUpdateStickyNote,
  useDeleteStickyNote,
} from '@/hooks/use-staffing-board-notes';
import type { StickyNoteColor } from '@/hooks/use-staffing-board-notes';

interface StickyNotesPanelProps {
  canEdit: boolean;
  readOnlyReason?: string;
  timeZone?: string;
}

export function StickyNotesPanel({ canEdit, readOnlyReason, timeZone = 'UTC' }: StickyNotesPanelProps) {
  const { data: notes, isLoading, error } = useStickyNotes();
  const createMutation = useCreateStickyNote();
  const updateMutation = useUpdateStickyNote();
  const deleteMutation = useDeleteStickyNote();

  const [expanded, setExpanded] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [newColor, setNewColor] = useState<StickyNoteColor>('yellow');
  const createInputRef = useRef<HTMLTextAreaElement>(null);

  // Pointer-drag state for moving notes
  const dragRef = useRef<{ noteId: number; startX: number; startY: number; origX: number; origY: number } | null>(null);
  const [dragOffset, setDragOffset] = useState<{ noteId: number; dx: number; dy: number } | null>(null);

  useEffect(() => {
    if (isCreating && createInputRef.current) {
      createInputRef.current.focus();
    }
  }, [isCreating]);

  const handleCreate = useCallback(() => {
    const trimmed = newContent.trim();
    if (!trimmed) return;
    createMutation.mutate(
      { content: trimmed, color: newColor },
      {
        onSuccess: () => {
          setNewContent('');
          setNewColor('yellow');
          setIsCreating(false);
          toast.success('Note added');
        },
        onError: () => {
          toast.error('Failed to create note');
        },
      },
    );
  }, [newContent, newColor, createMutation]);

  const handleUpdate = useCallback(
    (payload: { id: number; content?: string; color?: StickyNoteColor; position_x?: number; position_y?: number }) => {
      updateMutation.mutate(payload, {
        onError: () => {
          toast.error('Failed to update note');
        },
      });
    },
    [updateMutation],
  );

  const handleDelete = useCallback(
    (id: number) => {
      deleteMutation.mutate(id, {
        onSuccess: () => {
          toast.success('Note deleted');
        },
        onError: () => {
          toast.error('Failed to delete note');
        },
      });
    },
    [deleteMutation],
  );

  const handleMoveStart = useCallback(
    (noteId: number, e: React.PointerEvent) => {
      const note = notes?.find((n) => n.id === noteId);
      if (!note) return;
      e.preventDefault();
      dragRef.current = {
        noteId,
        startX: e.clientX,
        startY: e.clientY,
        origX: note.position_x,
        origY: note.position_y,
      };

      const handlePointerMove = (ev: PointerEvent) => {
        if (!dragRef.current) return;
        setDragOffset({
          noteId: dragRef.current.noteId,
          dx: ev.clientX - dragRef.current.startX,
          dy: ev.clientY - dragRef.current.startY,
        });
      };

      const handlePointerUp = (ev: PointerEvent) => {
        if (dragRef.current) {
          const dx = ev.clientX - dragRef.current.startX;
          const dy = ev.clientY - dragRef.current.startY;
          if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
            handleUpdate({
              id: dragRef.current.noteId,
              position_x: dragRef.current.origX + dx,
              position_y: dragRef.current.origY + dy,
            });
          }
          dragRef.current = null;
          setDragOffset(null);
        }
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
    },
    [notes, handleUpdate],
  );

  const handleCreateKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleCreate();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsCreating(false);
        setNewContent('');
      }
    },
    [handleCreate],
  );

  const noteCount = notes?.length ?? 0;

  return (
    <div className="print:hidden" data-testid="sticky-notes-panel">
      {/* Header */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
        data-testid="sticky-notes-toggle"
      >
        <span className="flex items-center gap-2">
          <StickyNoteIcon className="h-4 w-4 text-amber-500" />
          Notes
          {noteCount > 0 && (
            <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-100 px-1.5 text-xs font-semibold text-amber-700">
              {noteCount}
            </span>
          )}
        </span>
        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {/* Expandable body */}
      {expanded && (
        <div className="mt-2 space-y-2" data-testid="sticky-notes-body">
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-6" data-testid="sticky-notes-loading">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          )}

          {/* Error state */}
          {error && (
            <div
              className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              data-testid="sticky-notes-error"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>Failed to load notes.</span>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && noteCount === 0 && !isCreating && (
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-5 text-center" data-testid="sticky-notes-empty">
              <StickyNoteIcon className="mx-auto h-6 w-6 text-gray-300" />
              <p className="mt-1 text-sm text-gray-500">No board notes yet.</p>
              {canEdit && (
                <button
                  onClick={() => setIsCreating(true)}
                  className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                  data-testid="sticky-notes-empty-create"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add first note
                </button>
              )}
              {!canEdit && readOnlyReason && (
                <p className="mt-1 text-xs text-gray-400">{readOnlyReason}</p>
              )}
            </div>
          )}

          {/* Notes list */}
          {!isLoading && !error && notes && notes.length > 0 && (
            <div className="space-y-2" data-testid="sticky-notes-list">
              {notes.map((note) => (
                <div
                  key={note.id}
                  style={
                    dragOffset?.noteId === note.id
                      ? { transform: `translate(${dragOffset.dx}px, ${dragOffset.dy}px)`, zIndex: 50, position: 'relative' as const }
                      : undefined
                  }
                >
                  <StickyNoteCard
                    note={note}
                    canEdit={canEdit}
                    onUpdate={handleUpdate}
                    onDelete={handleDelete}
                    onMoveStart={handleMoveStart}
                    timeZone={timeZone}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Create new note form */}
          {isCreating && (
            <div
              id="sticky-note-create-fields"
              className="rounded-lg border border-amber-300 bg-yellow-50 p-2 shadow-sm"
              data-testid="sticky-notes-create-form"
            >
              <textarea
                ref={createInputRef}
                value={newContent}
                onChange={(e) => setNewContent(e.target.value.slice(0, 500))}
                onKeyDown={handleCreateKeyDown}
                className="w-full resize-none rounded border-0 bg-transparent text-sm text-gray-800 placeholder-gray-400 focus:outline-none"
                required
                rows={3}
                maxLength={500}
                placeholder="Type your note..."
                data-testid="sticky-notes-create-textarea"
              />
              <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-1">
                  {(['yellow', 'blue', 'green', 'pink', 'orange', 'purple'] as StickyNoteColor[]).map((c) => {
                    const dotClasses: Record<StickyNoteColor, string> = {
                      yellow: 'bg-yellow-400',
                      blue: 'bg-blue-400',
                      green: 'bg-green-400',
                      pink: 'bg-pink-400',
                      orange: 'bg-orange-400',
                      purple: 'bg-purple-400',
                    };
                    return (
                      <button
                        key={c}
                        onClick={() => setNewColor(c)}
                        className={`h-5 w-5 rounded-full ${dotClasses[c]} border-2 transition-transform hover:scale-110 ${
                          c === newColor ? 'border-gray-800 scale-110' : 'border-transparent'
                        }`}
                        title={c}
                        data-testid={`sticky-notes-create-color-${c}`}
                      />
                    );
                  })}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-gray-400">{newContent.length}/500</span>
                  <button
                    onClick={() => { setIsCreating(false); setNewContent(''); }}
                    className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 rounded hover:bg-black/5"
                    data-testid="sticky-notes-create-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    data-validate-required="true"
                    data-validation-scope="sticky-note-create-fields"
                    onClick={handleCreate}
                    disabled={createMutation.isPending}
                    className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="sticky-notes-create-submit"
                  >
                    {createMutation.isPending ? 'Saving…' : 'Add'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add note button */}
          {canEdit && !isCreating && noteCount > 0 && (
            <button
              onClick={() => setIsCreating(true)}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-500 hover:border-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
              data-testid="sticky-notes-add-btn"
            >
              <Plus className="h-3.5 w-3.5" />
              Add note
            </button>
          )}
        </div>
      )}
    </div>
  );
}
